import { isNotificationType } from "@ondo/shared";
import type {
  NotificationDelivery,
  NotificationDigestResponse,
  NotificationPreference,
  NotificationType,
} from "@ondo/shared";

import { describeApiError } from "../lib/apiError.ts";
import {
  DIGEST_COPY,
  DIGEST_HOUR_OPTIONS,
  GROUP_OF,
  PREFERENCES_COPY,
  PREFERENCE_GROUPS,
  PREFERENCE_LABELS,
  SILENCED,
} from "./notificationPreferences.constants.ts";
import type { PreferenceGroupId } from "./notificationPreferences.constants.ts";

/** One line on the screen: a kind, what it is called, and how it reaches you. */
export interface PreferenceRow {
  type: NotificationType;
  label: string;
  delivery: NotificationDelivery;
  /**
   * The line that says what this person has stopped being told, or null.
   *
   * Decided here rather than in the view, because "is this a money or title
   * notification that is currently switched off" is a rule and the view may not
   * hold rules. Null for every other state, including a batched money row — a
   * digest is still being told.
   */
  silenced: string | null;
  /** True while this row's own write is in flight. Per row, not per screen. */
  saving: boolean;
}

export interface PreferenceGroup {
  id: PreferenceGroupId;
  title: string;
  blurb: string;
  rows: PreferenceRow[];
}

/**
 * The digest control — OND-96.
 *
 * NULL WHEN THE TIME COULD NOT BE LOADED, and the screen then renders the kinds
 * without it rather than failing. The two reads are independent: a digest hour
 * that did not arrive leaves every control below it correct, and a settings
 * screen that refused to open over a missing time would be worse than one
 * missing a section.
 */
export interface DigestControl {
  hourUtc: number;
  /** The label for the hour currently in force, e.g. `09:00 UTC`. */
  label: string;
  /**
   * The sentence under the control.
   *
   * Decided here rather than in the view, because "has this principal chosen or
   * are they on the default" is a rule and the view may not hold rules. They
   * read the same number and mean different things: somebody who has not chosen
   * follows the product's default if it ever moves.
   */
  note: string;
  options: readonly { value: number; label: string }[];
  saving: boolean;
}

export type PreferencesView =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "open";
      lead: string;
      risk: string;
      digest: DigestControl | null;
      groups: PreferenceGroup[];
      /** Null unless the reader may look but not change — see `canWrite`. */
      readOnlyNote: string | null;
      /** The last write's failure, or null. One at a time: the screen saves one row. */
      error: string | null;
      saved: boolean;
    };

/**
 * Turn the API's flat list into the sections the screen renders.
 *
 * EVERY GROUP THAT HAS ROWS, IN `PREFERENCE_GROUPS` ORDER, and within a group
 * the order the API sent — which is `NOTIFICATION_TYPES` order, itself ordered
 * by how much each kind wants from the reader. So neither this function nor the
 * view sorts anything: two orders are carried, and a third invented here would
 * be a third opinion.
 *
 * A KIND THIS BUILD HAS NEVER HEARD OF IS DROPPED. `isNotificationType` exists
 * precisely because "readers must tolerate a type they have never heard of
 * rather than fail on it", and the tolerance has to be here as well as in the
 * guard: a deployment where the API is ahead of the browser is an ordinary
 * minute of a deploy, and a settings screen that threw during it would be worse
 * than one missing a row it has no words for.
 */
export function toGroups(
  preferences: readonly NotificationPreference[],
  pending: Readonly<Record<string, boolean>> = {},
): PreferenceGroup[] {
  const known = preferences.filter((preference) => isNotificationType(preference.type));

  return PREFERENCE_GROUPS.flatMap((group) => {
    const rows = known
      .filter((preference) => GROUP_OF[preference.type] === group.id)
      .map((preference) => toRow(preference, pending[preference.type] ?? false));

    return rows.length === 0 ? [] : [{ ...group, rows }];
  });
}

function toRow(preference: NotificationPreference, saving: boolean): PreferenceRow {
  return {
    type: preference.type,
    label: PREFERENCE_LABELS[preference.type],
    delivery: preference.delivery,
    silenced: preference.delivery === "off" ? (SILENCED[preference.type] ?? null) : null,
    saving,
  };
}

/**
 * The whole screen state, assembled.
 *
 * AN ERROR LOADING BEATS EVERYTHING; a failed WRITE does not. The two are
 * different facts: if the list never arrived there is nothing truthful to show,
 * while a write that failed leaves the rest of the screen correct and one
 * control wrong, which is exactly what the inline message is for.
 */
export function resolve(input: {
  preferences: readonly NotificationPreference[] | null;
  pending: Readonly<Record<string, boolean>>;
  digest: NotificationDigestResponse | null;
  digestSaving: boolean;
  loadError: string | null;
  writeError: string | null;
  saved: boolean;
  canWrite: boolean;
  isFirm: boolean;
}): PreferencesView {
  if (input.loadError) return { kind: "error", message: input.loadError };
  if (!input.preferences) return { kind: "loading" };

  return {
    kind: "open",
    lead: input.isFirm ? PREFERENCES_COPY.firmLead : PREFERENCES_COPY.lead,
    risk: PREFERENCES_COPY.risk,
    digest: toDigest(input.digest, input.digestSaving),
    groups: toGroups(input.preferences, input.pending),
    readOnlyNote: input.canWrite ? null : PREFERENCES_COPY.readOnlyNote,
    error: input.writeError,
    saved: input.saved,
  };
}

/**
 * The list with one kind changed.
 *
 * Applied before the server answers and again from its answer, which is not
 * redundant: the first is what makes a radio feel like a radio, and the second
 * is what makes the screen say what was actually written. `set` is the same
 * function both times, so the two cannot disagree about shape.
 */
export function withDelivery(
  preferences: readonly NotificationPreference[],
  type: NotificationType,
  delivery: NotificationDelivery,
): NotificationPreference[] {
  return preferences.map((preference) =>
    preference.type === type ? { ...preference, delivery } : preference,
  );
}

/**
 * The digest control, or nothing.
 *
 * THE LABEL IS LOOKED UP RATHER THAN FORMATTED, so the sentence under the
 * control and the option inside it cannot disagree about how an hour is
 * written. An hour with no option is an hour this build does not offer — which
 * cannot happen while `DIGEST_HOUR_OPTIONS` is derived from the same list the
 * door validates against, and falls back to the raw hour rather than to an
 * empty sentence if it ever does.
 */
export function toDigest(
  digest: NotificationDigestResponse | null,
  saving: boolean,
): DigestControl | null {
  if (!digest) return null;

  const label =
    DIGEST_HOUR_OPTIONS.find((option) => option.value === digest.hourUtc)?.label ??
    `${String(digest.hourUtc).padStart(2, "0")}:00 UTC`;

  return {
    hourUtc: digest.hourUtc,
    label,
    note: digest.chosen ? DIGEST_COPY.chosenNote(label) : DIGEST_COPY.unchosenNote,
    options: DIGEST_HOUR_OPTIONS,
    saving,
  };
}

export function describeDigestError(error: unknown): string {
  return describeApiError(error, DIGEST_COPY.unknownError);
}

export function describeError(error: unknown): string {
  return describeApiError(error, PREFERENCES_COPY.unknownError);
}

export function describeLoadError(error: unknown): string {
  return describeApiError(error, PREFERENCES_COPY.loadError);
}
